import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button, Input } from '@sighthost/ui';
import { siteConfig } from '@sighthost/config';
import { supabase } from '@/shared/lib/supabase';
import { useAuth } from '@/shared/contexts/AuthContext';
import { PASSWORD_MIN_LENGTH, validatePassword } from '@/shared/utils/passwordPolicy';
import { defaultRouteForUser, defaultRouteForRole, isStaffUser, isInternalUser } from '@/shared/components/routing/defaultRouteForUser';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import {
  AuthLayout,
  loadWizardPlan,
  type AuthVariant,
} from '../components';

/**
 * SignInPage — Supabase Auth email + password sign-in.
 *
 * Three modes, switched by URL hash + state:
 *   1. `signin`        — default. Email + password.
 *   2. `forgot`        — request a password-reset email.
 *   3. `set-password`  — entered when the URL hash carries a Supabase invite
 *                          or recovery token (set-after-invite, or after the
 *                          customer clicks the "forgot password" email link).
 *
 * See `_bmad-output/USER_LIFECYCLE_AND_WHM_LINKING.md` §5 and §6.
 */

type Mode = 'signin' | 'forgot' | 'forgot-sent' | 'set-password' | 'verifying' | 'mfa-challenge';

/**
 * Module-level guard against StrictMode double-mount + browser back/forward.
 * PKCE codes are single-use; calling exchangeCodeForSession twice with the
 * same code returns "Auth session is missing". Tracking attempted codes here
 * (instead of in component state) makes the guard survive component remount.
 */
const CONSUMED_CODES = new Set<string>();

export default function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // LD-O6 — `?from=` carries a return path through the email-verification
  // redirect (router state is lost across the email round-trip). Only same-
  // origin paths are accepted to avoid open-redirect.
  const fromQuery = (() => {
    const raw = searchParams.get('from');
    if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
    return raw;
  })();
  const { isAuthenticated, loading, user } = useAuth();
  // INCIDENT 2026-08-03: post-auth navigation used defaultRouteForUser(), which
  // reads only app_metadata.role. Phase 11 external sales staff carry none
  // (SP-18), so every agent without the `support` role was sent to /app — the
  // customer portal. Resolve the sales role from staff_profile and route on it.
  const { loading: roleLoading, isSalesStaff } = useStaffRole();
  /** Where this user belongs once BOTH auth and staff_profile have resolved. */
  const landingRoute = defaultRouteForRole(user, isSalesStaff);

  const [mode, setMode] = useState<Mode>('signin');
  // 'lead-claim' is Phase 11 SP-21 — verified via Supabase as type='invite' but
  // tagged separately so the variant resolver picks the warmer left-panel copy.
  const [tokenKind, setTokenKind] = useState<'invite' | 'recovery' | 'lead-claim' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // MFA challenge state (set after password sign-in succeeds and the session
  // needs an AAL2 step). The factorId is the verified TOTP factor we'll
  // challenge against; the code is the 6-digit token from the user's app.
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  // Latches `true` once we know this session is here for password recovery —
  // either via the URL hash (synchronous, on first paint) or via the
  // PASSWORD_RECOVERY auth event (async, after detectSessionInUrl consumed
  // the hash). Used to suppress the "already signed in → redirect to dashboard"
  // early-return below.
  const recoveryLatched = useRef(false);

  // Detect the recovery flow regardless of whether Supabase is using the
  // implicit-flow (#access_token=…&type=recovery) or PKCE flow (?code=…).
  // We tag every recovery redirect with `?recovery=1` so the intent survives
  // Supabase's verify→redirect bounce, and we also pre-detect the legacy
  // hash for back-compat with in-flight emails. Errors (e.g. otp_expired)
  // arrive as `?error=…&error_code=…&error_description=…` — render them.
  useEffect(() => {
    // 1. Inspect query string for our intent tag, error params, and PKCE code.
    const url = new URL(window.location.href);
    const qs = url.searchParams;
    const isRecoveryTagged = qs.get('recovery') === '1';
    const isInviteTagged = qs.get('invite') === '1';
    const isLeadClaimTagged = qs.get('lead_claim') === '1';
    const errorCode = qs.get('error_code');
    const errorDescription = qs.get('error_description');
    const hasPkceCode = qs.has('code');

    if (errorCode || qs.get('error')) {
      setError(humaniseAuthError(errorDescription || errorCode || 'Link is no longer valid.'));
      ['error', 'error_code', 'error_description', 'recovery', 'invite', 'lead_claim', 'code'].forEach((k) => qs.delete(k));
      window.history.replaceState(null, '', `${url.pathname}${qs.toString() ? `?${qs}` : ''}`);
      return;
    }

    // 2. Inspect hash for legacy implicit-flow recovery/invite tokens.
    //
    //    INCIDENT 2026-09-07: this branch switched to `set-password` WITHOUT
    //    ever establishing a client session. `detectSessionInUrl` is false
    //    (memory/auth_recovery_pkce_fix.md), so the SDK never read the
    //    `#access_token=…` fragment either — and `updateUser` then failed
    //    with "Auth session missing", humanised as "your link has expired".
    //    Every GoTrue-default invite link since 2026-05-04 landed here; every
    //    agent who got in did so via a staff-issued token_hash reset link.
    //    The auth-email-hook now rewrites invite/recovery links to the
    //    token_hash form (branch 3a), so this branch only serves links minted
    //    before the hook was registered or by a GoTrue template we do not
    //    control (signup / email_change). Adopt the session from the fragment
    //    explicitly; if that cannot be done, say so honestly.
    const hash = window.location.hash;
    if (hash) {
      const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
      const type = hashParams.get('type');
      if (type === 'invite' || type === 'recovery') {
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        recoveryLatched.current = true;
        setTokenKind(type);
        window.history.replaceState(null, '', window.location.pathname);
        if (!accessToken || !refreshToken) {
          recoveryLatched.current = false;
          setMode('signin');
          setError('This link did not carry a usable sign-in token. Request a new one below.');
          return;
        }
        setMode('verifying');
        supabase.auth
          .setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(({ error: sessErr }) => {
            if (sessErr) {
              recoveryLatched.current = false;
              setMode('signin');
              setError(
                'This link has expired or has already been used. Request a new one below.',
              );
              return;
            }
            setMode('set-password');
          });
        return;
      }
      if (type === 'signup' || hashParams.get('access_token')) {
        navigate(landingRoute, { replace: true });
        return;
      }
    }

    // 3a. token_hash recovery (preferred — server-generated link via
    //     admin.generateLink). The portal calls supabase.auth.verifyOtp
    //     directly, which mints the session without needing a PKCE code
    //     verifier (which we don't have when the link was server-generated).
    //
    //     Phase 11 SP-21: `lead-claim` links carry `?lead_claim=1` + token_hash.
    //     The underlying Supabase OTP type is `'invite'` (we reuse the Phase 2
    //     `claim_existing` template) — only the `tokenKind` state diverges so
    //     the variant resolver picks the warmer left-panel copy.
    const tokenHash = qs.get('token_hash');
    if (tokenHash && (isRecoveryTagged || isInviteTagged || isLeadClaimTagged)) {
      // Supabase verify type — lead-claim verifies as invite under the hood.
      const verifyType: 'invite' | 'recovery' = isRecoveryTagged ? 'recovery' : 'invite';
      // tokenKind state — drives the variant resolver + post-set-password destination.
      const kind: 'invite' | 'recovery' | 'lead-claim' =
        isLeadClaimTagged ? 'lead-claim' : isRecoveryTagged ? 'recovery' : 'invite';
      recoveryLatched.current = true;
      setTokenKind(kind);
      setMode('verifying');
      // Strip the token_hash + flow tags so refresh doesn't re-attempt.
      ['token_hash', 'recovery', 'invite', 'lead_claim', 'type'].forEach((k) => qs.delete(k));
      window.history.replaceState(null, '', `${url.pathname}${qs.toString() ? `?${qs}` : ''}`);

      if (CONSUMED_CODES.has(tokenHash)) {
        // StrictMode double-mount — first mount already attempted. Check
        // whether it succeeded.
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session) {
            setMode('set-password');
          } else {
            recoveryLatched.current = false;
            setMode('signin');
            setTokenKind(null);
            setError(
              'This link has expired or has already been used. Request a new one below.',
            );
          }
        });
      } else {
        CONSUMED_CODES.add(tokenHash);
        supabase.auth
          .verifyOtp({ token_hash: tokenHash, type: verifyType })
          .then(({ data, error: vErr }) => {
            if (vErr || !data?.session) {
              recoveryLatched.current = false;
              setMode('signin');
              setTokenKind(null);
              setError(
                'This link has expired or has already been used. Request a new one below.',
              );
              return;
            }
            setMode('set-password');
          });
      }
      return;
    }

    // 3b. PKCE recovery/invite (legacy fallback): the URL carries `?code=…`.
    //     This was the original path when admin.generateLink's action_link
    //     was used directly — Supabase's /verify endpoint converts the
    //     token to a PKCE code on redirect. Kept for back-compat with
    //     any in-flight emails issued before the token_hash flow shipped.
    const code = qs.get('code');
    if (hasPkceCode && code) {
      recoveryLatched.current = true;
      setTokenKind(isLeadClaimTagged ? 'lead-claim' : isInviteTagged ? 'invite' : 'recovery');
      setMode('verifying');
      // Strip `code` + flow tags from the URL so a refresh doesn't re-attempt.
      ['code', 'recovery', 'invite', 'lead_claim'].forEach((k) => qs.delete(k));
      window.history.replaceState(null, '', `${url.pathname}${qs.toString() ? `?${qs}` : ''}`);

      if (CONSUMED_CODES.has(code)) {
        // Already attempted in a prior mount this session. Check if exchange
        // succeeded (session exists) — if so, proceed; if not, show error.
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session) {
            setMode('set-password');
          } else {
            recoveryLatched.current = false;
            setMode('signin');
            setTokenKind(null);
            setError(
              'This password-reset link has expired or has already been used. Request a new one below.',
            );
          }
        });
      } else {
        CONSUMED_CODES.add(code);
        supabase.auth.exchangeCodeForSession(code).then(({ data, error: exErr }) => {
          // Treat "no session returned" as failure even when error is null —
          // happens with PKCE codes generated server-side without a matching
          // client verifier. Without this guard, mode flips to 'set-password'
          // but updateUser later fails with "Auth session missing!".
          if (exErr || !data?.session) {
            recoveryLatched.current = false;
            setMode('signin');
            setTokenKind(null);
            setError(
              'This password-reset link has expired or has already been used. Request a new one below.',
            );
            return;
          }
          setMode('set-password');
        });
      }
    }

    // 4. Subscribe to PASSWORD_RECOVERY for implicit-flow back-compat. PKCE
    //    flow only fires SIGNED_IN, which is why the synchronous tag check
    //    above is the primary path.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoveryLatched.current = true;
        setTokenKind('recovery');
        setMode('set-password');
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate, user]);

  // If already signed in AND we are not handling a recovery/invite flow,
  // redirect to the role-appropriate landing page. The `recoveryLatched` ref
  // is what protects against the race where Supabase consumes the recovery
  // hash + sets `isAuthenticated=true` before our state-setters run.
  if (
    !loading &&
    // Wait for staff_profile too — resolving before it lands would route a
    // sales agent as a customer (incident 2026-08-03).
    !roleLoading &&
    isAuthenticated &&
    mode !== 'set-password' &&
    mode !== 'verifying' &&
    mode !== 'mfa-challenge' &&
    !recoveryLatched.current
  ) {
    const fromState = (location.state as { from?: string } | null)?.from ?? fromQuery ?? undefined;
    const target =
      fromState && isAppropriateForUser(fromState, user)
        ? fromState
        : landingRoute;
    return <Navigate to={target} replace />;
  }

  const fromState = (location.state as { from?: string } | null)?.from ?? fromQuery ?? undefined;
  const targetAfterSignIn =
    fromState && isAppropriateForUser(fromState, user)
      ? fromState
      : landingRoute;

  async function onSignIn(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (signInError) {
        setError(humaniseAuthError(signInError.message));
        return;
      }

      // Phase 11 SP-21 — `sign_in_disabled` rejection. Leads imported via
      // `lead-batch-import` carry `app_metadata.sign_in_disabled=true` until
      // `lead-convert` clears the flag. If such an account somehow lands here
      // with valid credentials (rare — usually self-signup race), sign them
      // back out and explain. Cheapest correct path: Supabase Auth doesn't
      // expose a pre-sign-in metadata check, so we let sign-in succeed,
      // inspect, then sign out. Session is gone before they see anything past
      // the friendly error.
      const { data: { user: justSignedIn } } = await supabase.auth.getUser();
      if (justSignedIn?.app_metadata?.sign_in_disabled === true) {
        await supabase.auth.signOut();
        setError(
          "Your account hasn't been activated yet. Your sales contact will be in touch shortly with next steps.",
        );
        return;
      }

      // Check if the session needs an AAL2 step (TOTP). If the user has a
      // verified TOTP factor, signInWithPassword leaves the session at AAL1
      // and we must challenge a factor to upgrade to AAL2 before navigating.
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal && aal.nextLevel === 'aal2' && aal.currentLevel !== 'aal2') {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totp = factors?.totp?.find((f) => f.status === 'verified');
        if (totp) {
          setMfaFactorId(totp.id);
          setMode('mfa-challenge');
          return;
        }
      }

      navigate(targetAfterSignIn, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in — try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function onMfaSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!mfaFactorId) return;
    const cleaned = mfaCode.replace(/\s/g, '');
    if (!/^\d{6}$/.test(cleaned)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: verifyErr } = await supabase.auth.mfa.challengeAndVerify({
        factorId: mfaFactorId,
        code: cleaned,
      });
      if (verifyErr) {
        setError(humaniseAuthError(verifyErr.message));
        return;
      }
      navigate(targetAfterSignIn, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed — try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function isAppropriateForUser(path: string, u: typeof user): boolean {
    const internal = isInternalUser(u); // full staff OR scoped support
    if (path.startsWith('/admin')) return internal;
    if (path.startsWith('/app')) return !internal;
    return true; // public paths fine for either
  }

  async function onForgotSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Enter your email address first.');
      return;
    }
    setSubmitting(true);
    try {
      // Route through our own EF rather than supabase.auth.resetPasswordForEmail.
      // The EF generates the recovery link via supabase.auth.admin.generateLink
      // (server-side, no Supabase email sent) and dispatches our branded
      // PasswordResetEmail through notification-runner. The 200 success path
      // returns a generic message regardless of whether the email exists
      // (anti-enumeration); 429 paths surface clear rate-limit guidance to
      // the user (the per-email limit applies to known + unknown emails alike,
      // so showing it doesn't leak existence).
      const { error: efError } = await supabase.functions.invoke(
        'password-reset-request',
        { body: { email: email.trim().toLowerCase() } },
      );
      if (efError) {
        // Try to extract the EF's structured error body so we can show the
        // specific rate-limit copy. Supabase-js sets `efError.context` to the
        // raw Response on FunctionsHttpError; parse the body to get our code.
        let friendlyMsg: string | null = null;
        try {
          const ctx = (efError as unknown as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = (await ctx.clone().json()) as { code?: string; message?: string };
            if (body?.message) friendlyMsg = body.message;
          }
        } catch {
          // Body wasn't JSON or already consumed — fall through to humanise.
        }
        setError(friendlyMsg ?? humaniseAuthError(efError.message));
        return;
      }
      setMode('forgot-sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send reset email.');
    } finally {
      setSubmitting(false);
    }
  }

  async function onSetPasswordSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const validation = validatePassword(password, confirmPassword);
    if (validation) {
      setError(validation);
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(humaniseAuthError(updateError.message));
        return;
      }
      // Re-read the now-current user from the supabase client (the local
      // `user` from useAuth is the pre-update snapshot) so role-aware routing
      // sees the correct claims.
      const { data: refreshed } = await supabase.auth.getUser();
      const u = refreshed.user ?? user;
      // Where to send them next:
      //   - staff invite/recovery → /admin
      //   - customer invite       → /app/settings/billing-info to fill in any missing details
      //   - customer lead-claim   → /app/settings/billing-info (Phase 11 SP-21 — same flow as invite)
      //   - customer recovery     → /app dashboard
      const dest = isStaffUser(u)
        ? '/admin'
        : isInternalUser(u) // support agent
          ? '/admin/customers'
          : (tokenKind === 'invite' || tokenKind === 'lead-claim')
            ? '/app/settings/billing-info'
            : '/app';
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set password.');
    } finally {
      setSubmitting(false);
    }
  }

  // Variant resolution — picks the AuthLayout left-panel content based on
  // mode + token kind. Note: Google OAuth deferred per Mr Mampe 2026-05-17,
  // so the 'staff' variant has no current trigger here (path-based or
  // user-based detection adds complexity for a rare case; future code can
  // activate it without changing AuthLayout). All other variants ARE wired.
  // Spec: _bmad-output/AUTH_AND_TRUST_BRD_2026-05-17.md §5.3 +
  //       _bmad-output/PHASE_11_SALES_PORTAL_ARCHITECTURE_V0.2.md §16.1.
  const variant: AuthVariant = (() => {
    if (mode === 'set-password' && tokenKind === 'lead-claim') return 'lead-claim';
    if (mode === 'set-password' && tokenKind === 'invite') return 'invite-claim';
    if (mode === 'set-password' && tokenKind === 'recovery') return 'password-recovery';
    if (mode === 'forgot' || mode === 'forgot-sent' || mode === 'verifying') return 'password-recovery';
    if (loadWizardPlan() !== null) return 'wizard-return';
    return 'marketing-direct';
  })();

  return (
    <AuthLayout variant={variant}>
      <header className="mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight text-ink">
          {headerText(mode, tokenKind)}
        </h1>
      </header>

      <div className="rounded-md border border-border bg-card p-6 shadow-card">
          {mode === 'verifying' && (
            <div className="flex items-center justify-center gap-3 py-8 text-sm text-foreground-soft">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span>Verifying your reset link…</span>
            </div>
          )}

          {mode === 'forgot-sent' && (
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <p className="text-ink">
                  If an account exists for <strong>{email}</strong>, a reset link is on its way.
                </p>
              </div>
              <p className="text-foreground-soft">
                Check your inbox (and spam folder). The link expires in 1 hour.
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setMode('signin')}
              >
                Back to sign in
              </Button>
            </div>
          )}

          {mode === 'set-password' && (
            <form onSubmit={onSetPasswordSubmit} className="space-y-4">
              {tokenKind === 'invite' && (
                <p className="text-sm text-foreground-soft">
                  Welcome to {siteConfig.name}. Set a password to access your portal.
                  Your cPanel password (if you have one) is separate and unchanged.
                </p>
              )}
              {tokenKind === 'lead-claim' && (
                <p className="text-sm text-foreground-soft">
                  Welcome to {siteConfig.name}. Choose a password to finish setting
                  up the account your sales contact prepared for you.
                </p>
              )}
              {tokenKind === 'recovery' && (
                <p className="text-sm text-foreground-soft">
                  Choose a new password for your portal account.
                </p>
              )}
              <Field
                label="New password"
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete="new-password"
                disabled={submitting}
                autoFocus
                hint="Minimum 10 characters. Mix upper, lower, and a digit."
              />
              <Field
                label="Confirm new password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                type="password"
                autoComplete="new-password"
                disabled={submitting}
              />
              {error && <ErrorBanner message={error} />}
              <Button type="submit" variant="marketing" className="w-full text-xs" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? 'Setting password…' : 'Set password'}
              </Button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={onForgotSubmit} className="space-y-4">
              <Field
                label="Email"
                value={email}
                onChange={setEmail}
                type="email"
                autoComplete="email"
                disabled={submitting}
                autoFocus
              />
              {error && <ErrorBanner message={error} />}
              <Button type="submit" variant="marketing" className="w-full text-xs" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Send reset link
              </Button>
              <button
                type="button"
                onClick={() => {
                  setMode('signin');
                  setError(null);
                }}
                className="block w-full text-center text-xs text-muted-foreground hover:text-ink"
              >
                Back to sign in
              </button>
            </form>
          )}

          {mode === 'signin' && (
            <form onSubmit={onSignIn} className="space-y-4">
              <Field
                label="Email"
                value={email}
                onChange={setEmail}
                type="email"
                autoComplete="email"
                disabled={submitting}
                autoFocus
              />
              <Field
                label="Password"
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete="current-password"
                disabled={submitting}
              />
              {error && <ErrorBanner message={error} />}
              <Button type="submit" variant="marketing" className="w-full text-xs" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setMode('forgot');
                    setError(null);
                  }}
                  className="text-muted-foreground hover:text-primary hover:underline"
                >
                  Forgot password?
                </button>
                <Link
                  to="/signup"
                  className="text-muted-foreground hover:text-primary hover:underline"
                >
                  Create account →
                </Link>
              </div>
              <p className="pt-2 text-center text-[11px] text-muted-foreground">
                <a
                  href={siteConfig.url}
                  className="hover:text-primary hover:underline"
                >
                  ← Back to sighthost.online
                </a>
              </p>
            </form>
          )}

          {mode === 'mfa-challenge' && (
            <form onSubmit={onMfaSubmit} className="space-y-4">
              <p className="text-sm text-foreground-soft">
                Two-factor authentication is enabled on your account. Enter the
                6-digit code from your authenticator app to finish signing in.
              </p>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink-soft">
                  6-digit code
                </span>
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="000000"
                  maxLength={7}
                  autoFocus
                  disabled={submitting}
                  spellCheck={false}
                  className="text-center text-lg tracking-widest tabular-nums"
                />
              </label>
              {error && <ErrorBanner message={error} />}
              <Button type="submit" variant="marketing" className="w-full text-xs" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? 'Verifying…' : 'Verify and continue'}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Lost access to your authenticator?{' '}
                <a
                  href={`mailto:${siteConfig.supportEmail}`}
                  className="text-primary-ink hover:underline"
                >
                  Email support
                </a>
              </p>
            </form>
          )}
      </div>
    </AuthLayout>
  );
}

function headerText(mode: Mode, tokenKind: 'invite' | 'recovery' | 'lead-claim' | null): string {
  if (mode === 'verifying') return 'Verifying your link…';
  if (mode === 'set-password') {
    if (tokenKind === 'invite' || tokenKind === 'lead-claim') return 'Set your portal password';
    return 'Choose a new password';
  }
  if (mode === 'forgot' || mode === 'forgot-sent') return 'Reset your password';
  if (mode === 'mfa-challenge') return 'Two-factor authentication';
  return 'Sign in to your account';
}

// validatePassword now lives in @/shared/utils/passwordPolicy — see S-6.

function Field({
  label,
  value,
  onChange,
  type,
  autoComplete,
  disabled,
  autoFocus,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type: string;
  autoComplete: string;
  disabled?: boolean;
  autoFocus?: boolean;
  hint?: string;
}) {
  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);
  const effectiveType = isPassword && revealed ? 'text' : type;

  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-ink-soft">
        {label}
      </span>
      <div className={isPassword ? 'relative' : undefined}>
        <Input
          type={effectiveType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          disabled={disabled}
          autoFocus={autoFocus}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          className={isPassword ? 'pr-10' : undefined}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            disabled={disabled}
            tabIndex={-1}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-ink focus-visible:outline-none focus-visible:text-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </label>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-md border border-danger/30 bg-danger/10 p-3 text-sm text-danger"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function humaniseAuthError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('invalid login credentials')) {
    return 'Email or password is incorrect.';
  }
  if (m.includes('email not confirmed')) {
    return 'Please confirm your email — we sent a link when you signed up.';
  }
  if (m.includes('too many requests') || m.includes('rate limit')) {
    return 'Too many sign-in attempts. Please wait a few minutes and try again.';
  }
  if (m.includes('password') && m.includes('weak')) {
    return `Password is too weak. Use at least ${PASSWORD_MIN_LENGTH} characters with mixed case and a digit.`;
  }
  if (m.includes('expired') || m.includes('invalid token')) {
    return 'The link has expired. Request a new one from the sign-in page.';
  }
  if (m.includes('auth session missing') || m.includes('session not found')) {
    // Honest wording (2026-09-07): this is NOT an expired link. It means the
    // page reached the password form without a session — which for four
    // months was the invite link's normal behaviour. Do not call it expiry.
    return 'We could not start a secure session from that link. Request a new one from the sign-in page.';
  }
  return raw;
}
